import React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DownloadProgress, DownloadProgressBar } from '../ui/download-progress';
import { Folder, FileText, Download, Eye, Building2, Shield, Users, FileSignature, Home, ChevronDown, User, Tag, Hash, Clock, CheckCircle2, Sparkles } from 'lucide-react';

const SOURCE_COLORS = {
  DOMIUM: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  BATICO: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  ARCHOS: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  NOMINATIVE: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  AVENANT: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  MEMBRE: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  FTPE: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
  AUTRE: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' }
};

const SOURCE_ICONS = {
  DOMIUM: Home,
  BATICO: Shield,
  ARCHOS: Building2,
  NOMINATIVE: FileSignature,
  AVENANT: FileText,
  MEMBRE: Users,
  FTPE: Users,
  AUTRE: Folder
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const _formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function SourceIcon({ source }) {
  const Icon = SOURCE_ICONS[source] || Folder;
  return <Icon className="h-4 w-4" />;
}

// Collapsible document row component
function CollapsibleDocRow({ doc, isExpanded, onToggle, onDownload, onView, showSource = true, downloadStatus }) {
  const source = doc.source || doc.categorie || 'AUTRE';
  const colors = SOURCE_COLORS[source] || SOURCE_COLORS.AUTRE;
  const docId = doc.ged_id || doc.id;
  const label = doc.doc_type_label || doc.original_filename || doc.filename || 'Document';
  const reference = doc.reference || doc.description || '';
  const fileSize = doc.file_size || 0;
  const createdAt = doc.created_at || doc.uploaded_at;
  const userName = doc.enregistre_par_nom || [doc.user_prenom, doc.user_nom].filter(Boolean).join(' ') || doc.user_id || '';
  const userEspace = doc.enregistre_par_role || doc.user_espace || doc.user_role || '';
  const createdTime = doc.created_time || '';
  const createdDate = doc.created_date || '';
  const dlStatus = downloadStatus?.[docId];

  return (
    <div
      className={cn(
        "rounded-lg border transition-all duration-200",
        isExpanded ? "border-blue-200 shadow-md bg-white ring-1 ring-blue-100" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
      )}
      data-testid={`doc-row-${docId}`}
    >
      {/* Compact row */}
      <button
        onClick={() => onToggle(docId)}
        className="flex items-center gap-3 px-4 py-2.5 w-full text-left group"
        data-testid={`doc-toggle-${docId}`}
      >
        <div className={cn("h-8 w-8 rounded-md flex items-center justify-center shrink-0 transition-colors", colors.bg, "group-hover:ring-2 ring-offset-1", colors.border)}>
          <FileText className={cn("h-4 w-4", colors.text)} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={cn("font-medium text-sm truncate", isExpanded ? "text-blue-700" : "text-gray-900 group-hover:text-gray-700")}>{label}</p>
          <p className="text-[10px] text-gray-400 truncate">{reference || docId}</p>
        </div>
        {showSource && (
          <Badge variant="outline" className={cn("text-[10px] font-semibold border shrink-0 hidden sm:inline-flex", colors.text, colors.border)}>
            {source}
          </Badge>
        )}
        {dlStatus && <DownloadProgress status={dlStatus.status} progress={dlStatus.progress} />}
        <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
          <Clock className="h-3 w-3" />
          <span>{createdDate || formatDateShort(createdAt)}</span>
          {createdTime && <span className="text-gray-300">|</span>}
          {createdTime && <span className="font-mono text-[10px] text-gray-400">{createdTime}</span>}
        </div>
        {userName && (
          <div className="hidden lg:flex items-center gap-1 text-xs text-gray-500 shrink-0">
            <User className="h-3 w-3" />
            <span className="max-w-[100px] truncate">{userName}</span>
          </div>
        )}
        <span className="text-[10px] text-gray-400 tabular-nums shrink-0 hidden lg:inline">{formatFileSize(fileSize)}</span>
        <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-blue-500")} />
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`doc-details-${docId}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Col 1: Document */}
            <div className="space-y-2">
              <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Document</h4>
              <div className="space-y-1.5 text-sm">
                <p className="font-medium text-gray-900">{label}</p>
                {reference && (
                  <p className="text-gray-600 flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-gray-400" />
                    <span className="truncate">{reference}</span>
                  </p>
                )}
                <p className="text-gray-600 flex items-center gap-1.5">
                  <Hash className="h-3.5 w-3.5 text-gray-400" />
                  ID: <span className="font-mono text-xs">{docId}</span>
                </p>
                {showSource && (
                  <div className="text-gray-600 flex items-center gap-1.5">
                    <SourceIcon source={source} />
                    Source: <Badge variant="outline" className={cn("text-[10px]", colors.text, colors.border)}>{source}</Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Col 2: Horodatage & Identification */}
            <div className="space-y-2">
              <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Horodatage & Identification</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="text-gray-700">{createdDate || formatDateShort(createdAt)}</span>
                </div>
                {createdTime && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Heure:</span>
                    <span className="text-gray-700 font-mono text-xs">{createdTime}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Taille:</span>
                  <span className="text-gray-700">{formatFileSize(fileSize)}</span>
                </div>
                {userName && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Enregistre par:</span>
                    <span className="text-gray-700 flex items-center gap-1">
                      <User className="h-3 w-3 text-gray-400" />{userName}
                    </span>
                  </div>
                )}
                {userEspace && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Role:</span>
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5">{userEspace}</Badge>
                  </div>
                )}
                {doc.enregistre_par_email && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email:</span>
                    <span className="text-gray-700 text-xs truncate max-w-[150px]">{doc.enregistre_par_email}</span>
                  </div>
                )}
                {doc.statut && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Statut:</span>
                    <Badge className={cn("text-[10px]", doc.statut === 'valide' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>{doc.statut}</Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Col 3: Actions */}
            <div className="space-y-2">
              <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
              {dlStatus?.status === 'loading' && (
                <DownloadProgressBar status={dlStatus.status} progress={dlStatus.progress} filename={doc.filename} />
              )}
              <div className="flex flex-wrap gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onView(doc); }}
                  className="h-8 text-xs gap-1.5 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors"
                  data-testid={`view-doc-${docId}`}
                  disabled={dlStatus?.status === 'loading'}
                >
                  <Eye className="h-3.5 w-3.5" /> Ouvrir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onDownload(docId, doc.original_filename || doc.filename); }}
                  className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors"
                  data-testid={`download-doc-${docId}`}
                  disabled={dlStatus?.status === 'loading'}
                >
                  {dlStatus?.status === 'loading'
                    ? <><DownloadProgress status="loading" progress={dlStatus.progress} /> Telechargement...</>
                    : dlStatus?.status === 'done'
                      ? <><CheckCircle2 className="h-3.5 w-3.5" /> Telecharge</>
                      : <><Download className="h-3.5 w-3.5" /> Telecharger</>
                  }
                </Button>
                {doc.ged_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('klarys-demander-document', { detail: { gedId: doc.ged_id, nom: doc.title || doc.titre || doc.original_filename || doc.filename || 'document' } })); }}
                    className="h-8 text-xs gap-1.5 border-[#0a3d62]/30 text-[#0a3d62] hover:bg-sky-50 transition-colors"
                    data-testid={`klarys-doc-${docId}`}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Demander à Klarys
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { SOURCE_COLORS, SOURCE_ICONS, SourceIcon, CollapsibleDocRow };
